import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useAuthStore } from "@/features/auth/store/authStore"
import apiClient from "@/lib/api-client"

async function replaceProfilePhoto(
  userId: string,
  photoId: string,
  file: File,
): Promise<{ id: string; filePath: string; isCover: boolean }> {
  const formData = new FormData()
  formData.append("file", file)

  const response = await apiClient.put<{
    success: boolean
    data: { id: string; filePath: string; isCover: boolean }
    message: string
  }>(`api/profiles/${userId}/photos/${photoId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  })

  return response.data.data
}

export function useReplaceProfilePhoto() {
  const queryClient = useQueryClient()
  const user = useAuthStore(state => state.user)
  const setUser = useAuthStore(state => state.setUser)

  return useMutation({
    mutationFn: ({ userId, photoId, file }: { userId: string; photoId: string; file: File }) =>
      replaceProfilePhoto(userId, photoId, file),
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["photos", variables.userId] })

      // Update auth store - replace the photo entry
      if (user?.profile_photos) {
        const updatedPhotos = user.profile_photos.map(photo =>
          photo.id === variables.photoId
            ? { ...photo, id: data.id, filePath: data.filePath }
            : photo,
        )
        setUser({ ...user, profile_photos: updatedPhotos })
      }

      toast.success("Photo updated successfully")
    },
    onError: () => {
      toast.error("Failed to update photo")
    },
  })
}
